<script setup lang="ts">
    import { FormKitMessages } from '@formkit/vue';
    import Skeleton from '@shared/components/ui/skeleton.vue';
    import { FormSizeTypeEnum } from '@shared/constants/form-style';
    import { HelpersCountries } from '@shared/helpers';
    import { type ComputedRef, type Ref } from 'vue';

    const { t } = useI18n();

    const props = defineProps<{
        id?: string
        name: string
        prefixName: string
        validation?: string | [rule: string, ...args: unknown[]][]
        help?: string
        size?: FormSizeTypeEnum.NORMAL | FormSizeTypeEnum.EXTRA
        validationMessages?: Record<string, string>
        validationVisibility?: 'submit' | 'live' | 'blur' | 'dirty' | undefined,
        disabled?: boolean
        label?: string
        labelHidden?: boolean
        isLoading?: boolean
    }>();

    const { isDesktop, isMobile } = useAppBreakpoints();

    const phoneInput = ref();
    const errorMessages: Ref<(string | number | boolean)[]> = ref([]);

    const skeletonHeight = computed(() => isMobile.value ? '56px' : isDesktop.value ? '64px' : '60px');

    const countryOptions = Object.keys(HelpersCountries.countryPhonePrefixes)
        .map(countryCode => {
            const { translation, prefix } = HelpersCountries.getTranslationAndPrefix(countryCode);
            return {
                label: prefix,
                value: prefix,
                writtenAsset: t(translation),
            };
        });

    const isRequired: ComputedRef<boolean> = computed(() =>
        Array.isArray(props.validation) ? props.validation[0].includes('required') : props.validation?.includes('required') ?? false);
</script>

<template>
    <div
        class="phone-input"
        :class="{ 'phone-input--label-hidden': labelHidden }"
        :data-required="isRequired"
    >
        <span
            v-if="label && !isLoading"
            class="phone-input__label"
        >
            {{ label }}
        </span>
        <div class="phone-input__group">
            <div class="phone-input__inner">
                <Skeleton
                    v-if="isLoading"
                    :height="skeletonHeight"
                    width="124px"
                />
                <FormKit
                    v-else
                    :id="`${id ?? name ?? 'prefix'}-prefix`"
                    :options="countryOptions"
                    :wrapper-class="size ?? FormSizeTypeEnum.NORMAL"
                    :disabled="disabled"
                    :name="prefixName"
                    dropdown-wrapper-class="phone-input__list"
                    type="autocomplete"
                    placeholder="+00"
                    select-icon="chevronBottom"
                    outer-class="phone-input__prefix"
                    clear-search-on-open
                >
                    <template #option="{ option }">
                        <div
                            v-if="option?.writtenAsset || option?.label"
                            class="option"
                        >
                            <span v-if="option?.label">{{ option?.label }}</span>
                            <div
                                v-if="option?.writtenAsset"
                                class="ml-3"
                            >
                                <span>{{ option.writtenAsset }}</span>
                            </div>
                        </div>
                    </template>
                </FormKit>
                <Skeleton
                    v-if="isLoading"
                    :height="skeletonHeight"
                    width="100%"
                />
                <FormKit
                    v-else
                    :id="`${id ?? name ?? 'phone'}-phone`"
                    ref="phoneInput"
                    :name="name"
                    :label="label"
                    :validation="`length:8,32|number|${validation}`"
                    :validation-visibility="validationVisibility"
                    :disabled="disabled"
                    :wrapper-class="size ?? FormSizeTypeEnum.NORMAL"
                    :help="help"
                    :validation-messages="validationMessages"
                    type="tel"
                    placeholder="0 00 00 00 00"
                    label-class="formkit-label--hidden"
                    inner-class="phone-input__phone"
                />
            </div>
        </div>
        <div class="formkit-outer">
            <ul class="formkit-messages">
                <li
                    v-for="(message, index) in errorMessages"
                    :key="`message-${index}`"
                    class="formkit-message"
                >
                    {{ message }}
                </li>
            </ul>
            <FormKitMessages :node="phoneInput?.node" />
        </div>
    </div>
</template>

<style scoped lang="scss">
    .phone-input :deep(.formkit-outer) {
        &.phone-input__prefix {
            inline-size: var(--size-18);
        }

        .formkit-inner {
            input {
                box-sizing: border-box;
                padding: var(--spacing-03);
                border: 1px solid var(--border-tertiary);
                border-radius: var(--border-radius-xs);
                background-color: var(--background-form-input);
                block-size: var(--size-11);
                font-size: var(--font-medium);
                inline-size: 100%;
                line-height: 1;

                &::placeholder {
                    color: var(--text-placeholder);
                }

                &:hover,
                &:focus,
                &:focus-visible,
                &:focus-within {
                    background-color: var(--background-form-input-active);
                }

                @include min-width(md) {
                    block-size: var(--size-12);
                }

                @include min-width(lg) {
                    block-size: var(--size-13);
                }
            }

            &.secondary input {
                &:hover {
                    background-color: var(--background-form-input-hover);
                }

                &:focus,
                &:focus-visible,
                &:focus-within {
                    background-color: var(--background-form-input-active);
                }
            }

            .formkit-listbox-button {
                position: absolute;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                inset-block-start: 50%;
                inset-inline-end: var(--spacing-03);
                transform: translateY(-50%);

                > span {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
            }

            .phone-input__list {
                overflow: hidden;
                border: 1px solid var(--border-primary);
                border-radius: var(--border-radius-xs);
                background-color: var(--background-primary);
                margin-block: var(--spacing-02);
                visibility: hidden;

                ul {
                    min-inline-size: 304px;
                }

                ul li {
                    display: flex;
                    flex-direction: row-reverse;
                    align-items: center;
                    justify-content: space-between;
                    cursor: pointer;
                    min-block-size: var(--size-11);
                    padding-block: 0;
                    padding-inline: var(--spacing-03);

                    .option {
                        display: flex;
                        flex: 2;
                        align-items: center;
                        justify-content: flex-start;
                    }

                    .option span {
                        font-size: var(--font-regular);
                    }

                    &:not(:last-child) {
                        border-block-end: 1px solid var(--border-quaternary);
                    }

                    &[aria-selected=true] {
                        background-color: var(--background-primary-highlight);
                        padding-inline-end: var(--spacing-01);
                    }

                    &[aria-selected=true] .formkit-selected-icon {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    &[aria-selected=true] .formkit-selected-icon > svg {
                        block-size: var(--size-9);
                        inline-size: var(--size-9);
                    }

                    &:hover {
                        background-color: var(--background-primary-highlight);
                    }
                }

                &.right-display {
                    inset-inline-end: 0;
                }
            }
        }

        &[data-expanded=true] .formkit-inner {
            .phone-input__list {
                visibility: visible;
            }
        }

        &[data-invalid=true] .formkit-inner {
            input,
            &.secondary input {
                border-color: var(--border-negative);
            }
        }

        &[data-required=true] {
            .formkit-label::after {
                color: var(--text-secondary);
                content:" *";
            }
        }

        &[data-disabled=true] .formkit-inner {
            input,
            &.secondary input {
                border-color: var(--border-disabled-1);
                background-color: var(--background-disabled-1);
                cursor: not-allowed;
            }

            .formkit-listbox-button {
                display: none;
            }
        }
    }

    .phone-input {
        &__label {
            display: inline-block;
            padding: 0;
            color: var(--text-secondary);
            font-size: var(--font-regular);
            font-weight: var(--font-weight-light);
            margin-block-end: var(--spacing-01);

            &::first-letter {
                text-transform: capitalize;
            }
        }

        &__inner {
            display: flex;
            gap: var(--spacing-02);
        }

        // eslint-disable-next-line vue-scoped-css/no-unused-selector, vue-scoped-css/require-selector-used-inside
        .formkit-outer{
            flex: 2;
        }
    }

    .phone-input[data-required=true] :deep(.formkit-outer) {
        .formkit-label::after {
            color: var(--text-secondary);
            content:" *";
        }
    }
</style>
